import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { ordersApi } from "../services/services";
import { Order } from "../types";
import { useRequiredAuth } from "./shared/AuthContext";

const DeliveryNotePrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useRequiredAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    ordersApi
      .getOrderById(parseInt(id, 10))
      .then((orderData) => {
        setOrder(orderData);
        if (orderData.formData) {
          try {
            setFormData(JSON.parse(orderData.formData));
          } catch {
            setFormData({});
          }
        }
      })
      .catch((err) => setError(err.message || "Failed to load order"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center p-5">
        <p className="text-danger">{error || "Order not found"}</p>
        <Button variant="secondary" size="sm" onClick={() => window.close()}>
          Close
        </Button>
      </div>
    );
  }

  const formatDateTime = (dateStr: string | undefined) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const todayDate = formatDateTime(new Date().toISOString());

  const requiredDate = formatDateTime(order.dateRequired);

  const branch = formData.branch || order.clientName || order.branch || "";
  const orderTakenBy = order.orderTakenBy || "";
  const products: { name?: string; code?: string; quantity?: number }[] = formData.products || [];
  const notes = formData.requestNote || formData.requestOrNote || formData.notes || order.notes || "";

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {/* Toolbar - hidden when printing */}
      <div className="d-print-none" style={{ background: "#f8f9fa", borderBottom: "1px solid #dee2e6", padding: "12px 24px", position: "sticky", top: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h5 style={{ margin: 0 }}>Delivery Note Preview</h5>
        <div>
          <Button variant="success" size="sm" onClick={() => window.print()} className="me-2">
            <i className="bi bi-printer me-1"></i> Print Now
          </Button>
          <Button variant="secondary" size="sm" onClick={() => window.close()}>
            Close
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div id="delivery-note-content" style={{ maxWidth: "800px", margin: "0 auto", padding: "40px 32px", fontFamily: "Arial, sans-serif", color: "#000" }}>
        {/* Title */}
        <h2 style={{ textAlign: "center", marginBottom: "4px", fontSize: "16px", fontWeight: "bold" }}>
          Delivery Note
        </h2>

        {/* Department */}
        <p style={{ textAlign: "left", marginBottom: "20px", fontSize: "13px", fontWeight: "bold" }}>
          Department: {user.supplierName || ""}
        </p>

        {/* Info Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px", fontSize: "11px" }}>
          <tbody>
            <tr>
              <td style={{ border: "1px solid #000", padding: "4px 8px", fontWeight: "bold", width: "20%" }}>Date</td>
              <td style={{ border: "1px solid #000", padding: "4px 8px", width: "30%" }}>{todayDate}</td>
              <td style={{ border: "1px solid #000", padding: "4px 8px", fontWeight: "bold", width: "20%" }}>Branch</td>
              <td style={{ border: "1px solid #000", padding: "4px 8px", width: "30%" }}>{branch}</td>
            </tr>
            <tr>
              <td style={{ border: "1px solid #000", padding: "4px 8px", fontWeight: "bold" }}>Required Date</td>
              <td style={{ border: "1px solid #000", padding: "4px 8px" }}>{requiredDate}</td>
              <td style={{ border: "1px solid #000", padding: "4px 8px", fontWeight: "bold" }}>Order taken by</td>
              <td style={{ border: "1px solid #000", padding: "4px 8px" }}>{orderTakenBy}</td>
            </tr>
          </tbody>
        </table>

        {/* Products Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px", fontSize: "11px" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #000", padding: "4px 8px", textAlign: "left", fontWeight: "bold" }}>Products</th>
              <th style={{ border: "1px solid #000", padding: "4px 8px", textAlign: "left", fontWeight: "bold", width: "15%", textDecoration: "underline" }}>Qty</th>
            </tr>
          </thead>
          <tbody>
            {products.length > 0 ? (
              products.map((product, idx) => (
                <tr key={idx}>
                  <td style={{ border: "1px solid #000", padding: "4px 8px" }}>{product.name || product.code || ""}</td>
                  <td style={{ border: "1px solid #000", padding: "4px 8px" }}>{product.quantity ?? ""}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td style={{ border: "1px solid #000", padding: "4px 8px" }} colSpan={2}>No products</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Notes */}
        <p style={{ fontWeight: "bold", fontSize: "12px", marginBottom: "4px" }}>NOTES</p>
        <div style={{ border: "1px solid #000", padding: "10px", minHeight: "100px", fontSize: "11px" }}>
          {notes}
        </div>

        {/* Signature Lines */}
        <div style={{ marginTop: "40px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div style={{ width: "30%" }}>
            <div style={{ borderBottom: "1px solid #000", marginBottom: "4px", height: "20px" }}></div>
            <p style={{ fontSize: "10px", margin: 0 }}>Signature (Dispatched by)</p>
          </div>
          <div style={{ width: "30%" }}>
            <div style={{ borderBottom: "1px solid #000", marginBottom: "4px", height: "20px" }}></div>
            <p style={{ fontSize: "10px", margin: 0 }}>Signature (Received by)</p>
          </div>
          <div style={{ width: "25%" }}>
            <div style={{ borderBottom: "1px solid #000", marginBottom: "4px", height: "20px" }}></div>
            <p style={{ fontSize: "10px", margin: 0 }}>Date</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryNotePrint;
